import { useLocale } from '@/presentation/contexts/LocaleContext';
import { InAppPurchaseStatus } from '@/domain/enums/premiumPackages/InAppPurchaseStatus';
import { IdentityVerificationStatus } from '@/domain/enums/premiumPackages/IdentityVerificationStatus';
import { SupportCategory } from '@/domain/enums/support/supportCategory';
import { ChatMessageType } from '@/domain/enums/chatMessageType';
import { VersionUpdatedLegalDocument } from '@/domain/enums/versionUpdatedLegalDocument';
import type { ChatMessageEntity } from '@/domain/entities/chat/chatMessageEntity';
import {
  RoadmapItemStatus,
  ROADMAP_ITEM_STATUS_LABEL_KEYS,
} from '@/domain/enums/roadmap/roadmapItemStatus';
import { AddContentFlowType } from '@/domain/enums/publish/addContentFlowType';
import { isMessageFlowType } from '@/domain/utils/addContentFlowPredicates';

/**
 * Value-conditional translation lookups.
 *
 * This hook is **only** for translations whose label depends on a value at
 * call time (e.g., `getInAppPurchaseStatus(status)`). A resolver over a closed
 * domain enum that returns an array of **plain translated strings** is still a
 * label lookup and belongs here (e.g., `getAddContentStepTitles(flowType)`);
 * what must not live here is page data or component view-models — anything
 * carrying structure such as icons, ids, handlers, navigation targets, or
 * component props. Callers that have direct access to `useLocale()` should read
 * `l10n` themselves for those — typically inside a feature hook.
 */
export const useExpauseLocalizations = () => {
  const { l10n } = useLocale();

  const getInAppPurchaseStatus = (status: InAppPurchaseStatus): string => {
    const labels = l10n.premiumPackages.inAppPurchaseStatus;
    switch (status) {
      case InAppPurchaseStatus.none:
      case InAppPurchaseStatus.expired:
        return labels.subscribe;
      case InAppPurchaseStatus.pending:
        return labels.processing;
      case InAppPurchaseStatus.purchased:
      case InAppPurchaseStatus.restored:
        return labels.active;
      case InAppPurchaseStatus.error:
        return labels.error;
      case InAppPurchaseStatus.canceled:
        return labels.canceled;
      case InAppPurchaseStatus.paused:
        return labels.paused;
    }
  };

  const getVerificationLabel = (status: IdentityVerificationStatus | null | undefined): string => {
    const labels = l10n.premiumPackages.verificationStatus;
    if (status === null || status === undefined) {
      return labels.getVerified;
    }
    switch (status) {
      case IdentityVerificationStatus.requested:
        return labels.requested;
      case IdentityVerificationStatus.inReview:
        return labels.inReview;
      case IdentityVerificationStatus.declined:
        return labels.declined;
      case IdentityVerificationStatus.approved:
        return labels.approved;
    }
  };

  const getSubscribersTitle = (count: number): string => {
    if (count === 1) {
      return l10n.subscribers.titleSingular;
    }
    return l10n.subscribers.titlePlural.replace('{count}', String(count));
  };

  /**
   * Returns the localized display name for a `SupportCategory`. Mirrors
   * Flutter's `SupportCategoryExtension.displayName`
   * (lib/presentation/utils/support_utils.dart:130-150).
   */
  const getSupportCategoryDisplayName = (category: SupportCategory): string => {
    const labels = l10n.support.categoryLabels;
    switch (category) {
      case SupportCategory.account:
        return labels.account;
      case SupportCategory.appUsage:
        return labels.appUsage;
      case SupportCategory.premium:
        return labels.premium;
      case SupportCategory.wallet:
        return labels.wallet;
      case SupportCategory.privacy:
        return labels.privacy;
      case SupportCategory.report:
        return labels.report;
      case SupportCategory.supportRequest:
        return labels.supportRequest;
      case SupportCategory.privacyPolicy:
        return labels.policies;
      default:
        return labels.unknown;
    }
  };

  /**
   * Builds the unread-preview string for a peer message, mirroring the
   * Flutter content rule at `chat_screen.dart:3382-3395`.
   *
   *  - `ChatMessageType.text`  → the raw message body (`content`), or `null`
   *    if the body is empty/missing.
   *  - `ChatMessageType.gift`  → `chat.unread.gift`  with `{peerFirstName}`.
   *  - `ChatMessageType.image` → `chat.unread.photo` with `{peerFirstName}`.
   *  - `ChatMessageType.video` → `chat.unread.video` with `{peerFirstName}`.
   *  - other types → `null` (Flutter falls through with `content = null`).
   *
   * Used by `ChatPage` to format the most-recent unread item for
   * `UnreadBanner`'s pill copy.
   */
  const getUnreadPreview = (
    message: ChatMessageEntity,
    peerFirstName: string,
  ): string | null => {
    switch (message.type) {
      case ChatMessageType.gift:
        return l10n.chat.unread.gift.replace('{peerFirstName}', peerFirstName);
      case ChatMessageType.text:
        return message.content && message.content.length > 0
          ? message.content
          : null;
      case ChatMessageType.image:
        return l10n.chat.unread.photo.replace('{peerFirstName}', peerFirstName);
      case ChatMessageType.video:
        return l10n.chat.unread.video.replace('{peerFirstName}', peerFirstName);
      default:
        return null;
    }
  };

  /**
   * Returns the localized label for a roadmap item's `status` ordinal,
   * mirroring Flutter's `RoadmapItemStatus.values[item.status].description`
   * (lib/domain/enums/roadmap_item_status.dart:8-21). The ordinal→l10n-key
   * map lives in the domain enum (`ROADMAP_ITEM_STATUS_LABEL_KEYS`); each value
   * is a `roadmap.<leaf>` path resolved here against the `roadmap` bundle.
   * An out-of-range/unknown ordinal falls back to the "Open" label, matching
   * Flutter's lowest ordinal.
   */
  const getRoadmapItemStatusLabel = (status: number): string => {
    const labels = l10n.roadmap;
    const key =
      ROADMAP_ITEM_STATUS_LABEL_KEYS[status as RoadmapItemStatus] ??
      ROADMAP_ITEM_STATUS_LABEL_KEYS[RoadmapItemStatus.open];
    const leaf = key.replace('roadmap.', '') as keyof typeof labels;
    return labels[leaf];
  };

  /**
   * Per-step app-bar titles for the AddContent sheet, resolved from the single
   * `flowType` discriminator.
   *
   * - Message flows (chat / community / feedback / profileImage) → the two
   *   trimmed Drop + Preview titles. Feedback and profileImage deliberately
   *   inherit the chat copy rather than minting flow-specific keys (no
   *   behavioural gain; the image-only specialisation lives in the drop-step
   *   copy, via `resolveDropStepConfig`).
   * - The five-step user chain → step 1 is "Reply comment" for the
   *   comment-reply flow and the plain "Upload Video" otherwise. Flutter passes
   *   its `title` only to the PICKER screen's app bar
   *   (`add_content_screen.dart:1057-1062`); the later publish screens keep
   *   their own titles, hence steps 2-5 are identical for both.
   */
  const getAddContentStepTitles = (flowType: AddContentFlowType): string[] => {
    if (isMessageFlowType(flowType)) {
      return [
        l10n.publish.chatAddContentTitle,
        l10n.publish.chatPreviewTitle,
      ];
    }

    const firstStepTitle = flowType === AddContentFlowType.comment
      ? l10n.publish.commentReplyTitle
      : l10n.publish.addContentTitle;

    return [
      firstStepTitle,
      l10n.publish.previewTitle,
      l10n.publish.descriptionTitle,
      l10n.publish.settingsTitle,
      l10n.publish.monetizationTitle,
    ];
  };

  /**
   * The user-facing name of a legal document, used as the
   * `LegalDocumentUpdatedModal` title and as the tappable link inside its body
   * sentence. Flutter uses one string for both roles
   * (`version_updated_legal_document.dart:12-29`, `userFriendlyValue`, for the
   * link label; `remote_config_service.dart:237,248,259,270,281,292,303` for
   * the dialog title) — except on terms, where Flutter's own two strings
   * disagree in casing: "Terms of Service" as the dialog title
   * (`remote_config_service.dart:237`) vs "Terms Of Service" as the link label
   * (`version_updated_legal_document.dart:15`).
   *
   * Labels come from the web's existing `l10n.legal.*` block, not from copies of
   * Flutter's literals — the web already owns a translated (en + ro) name for
   * every one of these seven documents, the same names its legal pages render,
   * so minting duplicate title/link keys would be over-porting
   * (`conventions.md` → "Parity calibration — reuse beats literal porting").
   * One consequence is deliberate: the web calls the first document **"Terms of
   * Use"** (route `/legal/terms-of-use`) where Flutter says "Terms of Service" /
   * "Terms Of Service" — the modal must name the page it links to.
   *
   * The switch deliberately has **no `default`**: the non-`void` return type
   * makes a future eighth enum member fail the type-check ("function lacks
   * ending return statement") instead of silently rendering a blank label.
   */
  const getLegalDocumentLabel = (
    legalDocument: VersionUpdatedLegalDocument,
  ): string => {
    switch (legalDocument) {
      case VersionUpdatedLegalDocument.termsOfService:
        return l10n.legal.termsOfUse;
      case VersionUpdatedLegalDocument.coinsPolicy:
        return l10n.legal.coinsPolicy;
      case VersionUpdatedLegalDocument.communityGuidelines:
        return l10n.legal.communityGuidelines;
      case VersionUpdatedLegalDocument.exclusiveSubscriptionsPolicy:
        return l10n.legal.exclusiveSubscriptionsPolicy;
      case VersionUpdatedLegalDocument.premiumSubscriptionsPolicy:
        return l10n.legal.premiumSubscriptionsPolicy;
      case VersionUpdatedLegalDocument.privacyPolicy:
        return l10n.legal.privacyPolicy;
      case VersionUpdatedLegalDocument.rewardsPolicy:
        return l10n.legal.rewardsPolicy;
    }
  };

  return {
    expauseLocalizations:
    {
      getInAppPurchaseStatus,
      getVerificationLabel,
      getSubscribersTitle,
      getSupportCategoryDisplayName,
      getUnreadPreview,
      getRoadmapItemStatusLabel,
      getAddContentStepTitles,
      getLegalDocumentLabel,
    }
  };
};
